import React, { useState, useEffect } from "react";
import { Users2, Flame, Plus, Home } from "lucide-react";
import { getMatches, createApplicant } from "../../api";
import { useToast } from "../../hooks/use-toast";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const fmt = (n) => `\u00A3${Number(n || 0).toLocaleString()}`;
const empty = { name: "", email: "", phone: "", lookingFor: "buy", minBeds: 2, maxPrice: 500000, areas: "" };

const AdminMatching = () => {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const refresh = () => getMatches().then(setRows).catch(() => setRows([]));
  useEffect(() => { refresh(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await createApplicant({ ...form, minBeds: Number(form.minBeds), maxPrice: Number(form.maxPrice), areas: form.areas.split(",").map((s) => s.trim()).filter(Boolean) });
      toast({ title: "Applicant registered", description: "Auto-matched against current stock." });
      setOpen(false); setForm(empty); refresh();
    } catch { toast({ title: "Failed to register applicant" }); }
  };

  const input = "w-full h-[44px] px-3 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]";
  const totalHot = rows.reduce((a, r) => a + r.hotCount, 0);

  return (
    <div className="bg-white rounded-2xl border border-[#f0ece5] p-6" data-testid="admin-matching">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-display text-[20px] font-500 text-[#2b2b2b] flex items-center gap-2"><Users2 size={19} className="text-[#b8935f]" /> Applicant Matching</h3>
        <button onClick={() => setOpen(true)} data-testid="new-applicant-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#b8935f] text-white text-[13px] font-500 hover:bg-[#a67f4d] transition-colors"><Plus size={15} /> Register Applicant</button>
      </div>
      <p className="text-[13px] text-[#8a8a8a] mb-5">{rows.length} registered applicants · <span className="text-[#b8935f] font-500 inline-flex items-center gap-1"><Flame size={13} /> {totalHot} hot matches</span> ready to call.</p>

      <div className="space-y-4">
        {rows.map((r) => (
          <div key={r.applicant.id} className="p-4 rounded-xl bg-[#faf7f2] border border-[#f0ece5]" data-testid="applicant-card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-[15px] font-500 text-[#2b2b2b]">{r.applicant.name} <span className="text-[11px] uppercase tracking-wide text-[#b8935f] ml-2">{r.applicant.lookingFor}</span></p>
                <p className="text-[12px] text-[#8a8a8a]">{r.applicant.minBeds}+ beds · up to {fmt(r.applicant.maxPrice)}{r.applicant.areas?.length ? ` · ${r.applicant.areas.join(", ")}` : " · any area"}</p>
              </div>
              <span className={`text-[12px] px-3 py-1 rounded-full ${r.hotCount ? "bg-orange-100 text-orange-700" : "bg-gray-100 text-gray-500"}`}>{r.matchCount} match{r.matchCount !== 1 ? "es" : ""}{r.hotCount ? ` · ${r.hotCount} hot` : ""}</span>
            </div>
            {r.matches.length === 0 ? <p className="text-[12px] text-[#a0a0a0]">No current matches.</p> : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {r.matches.map((m) => (
                  <div key={m.propertyId} className={`flex items-center gap-2 p-2 rounded-lg border ${m.hot ? "border-orange-200 bg-orange-50/50" : "border-[#f0ece5] bg-white"}`} data-testid="match-item">
                    {m.image ? <img src={m.image} alt="" className="w-11 h-11 rounded object-cover" /> : <div className="w-11 h-11 rounded bg-[#f0ece5] flex items-center justify-center"><Home size={16} className="text-[#b8935f]" /></div>}
                    <div className="min-w-0">
                      <p className="text-[12px] font-500 text-[#2b2b2b] truncate">{m.title}</p>
                      <p className="text-[11px] text-[#8a8a8a]">{fmt(m.price)} · {m.score}%{m.hot ? " 🔥" : ""}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[460px]">
          <DialogTitle className="sr-only">Register Applicant</DialogTitle>
          <h2 className="font-display text-[24px] font-300 text-[#2b2b2b] mb-4">Register Applicant</h2>
          <form onSubmit={submit} className="space-y-3">
            <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} data-testid="applicant-name-input" />
            <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} data-testid="applicant-email-input" />
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.lookingFor} onValueChange={(v) => setForm({ ...form, lookingFor: v })}>
                <SelectTrigger className="h-[44px] border-[#e2d8c8]" data-testid="applicant-type-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="buy">Buying</SelectItem><SelectItem value="rent">Renting</SelectItem></SelectContent>
              </Select>
              <input type="number" min="1" placeholder="Min beds" value={form.minBeds} onChange={(e) => setForm({ ...form, minBeds: e.target.value })} className={input} data-testid="applicant-beds-input" />
            </div>
            <input type="number" placeholder="Max price / rent pcm" value={form.maxPrice} onChange={(e) => setForm({ ...form, maxPrice: e.target.value })} className={input} data-testid="applicant-price-input" />
            <input placeholder="Preferred areas (e.g. BA1, BA2) — blank for any" value={form.areas} onChange={(e) => setForm({ ...form, areas: e.target.value })} className={input} data-testid="applicant-areas-input" />
            <button type="submit" data-testid="submit-applicant-btn" className="w-full h-[48px] rounded-lg bg-[#b8935f] text-white text-[15px] font-500 hover:bg-[#a67f4d] transition-colors">Register & Match</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMatching;
